/*
Как работает система:
Первый запрос - загружает последние 100 объявлений без ограничений по дате
Следующие запросы - используют время последнего объявления как date2 (верхняя граница)
API возвращает объявления старше указанной даты
Результат - непрерывная лента объявлений в хронологическом порядке
*/
import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';
import {
  readOrganizationStorage,
  removeLegacyOrganizationStorage,
  writeOrganizationStorage,
} from '../tenancy/organizationStorage';
import apiClient from '../utils/apiClient';
import {
  Button,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Modal,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Avatar,
  Checkbox,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SearchIcon from '@mui/icons-material/Search';
import { prepareParseObject } from '../utils/prepareParseObject';
import objectStore from '../stores/objectStore';
import DynamicFieldsEditParsed from '../components/DynamicFieldsEditParsed';

// Списки категорий и типов сделок
const CATEGORIES = [
  { id: 2, name: 'Квартиры' },
  { id: 3, name: 'Комнаты' },
  { id: 4, name: 'Дома, дачи, коттеджи' },
  { id: 5, name: 'Земельные участки' },
  { id: 6, name: 'Гаражи и машиноместа' },
  { id: 7, name: 'Коммерческая недвижимость' },
  { id: 8, name: 'Недвижимость за рубежом' },
];

const PROPERTY_TYPES = [
  { id: 1, name: 'Продам' },
  { id: 2, name: 'Сдам' },
  { id: 3, name: 'Куплю' },
  { id: 4, name: 'Сниму' },
];

// Источники объявлений для ADS-API
const SOURCES = [
  { id: 1, name: 'Avito.ru' },
  { id: 3, name: 'Realty.Yandex.ru' },
  { id: 4, name: 'Cian.ru' },
  { id: 5, name: 'Sob.ru' },
  { id: 6, name: 'Youla.io' },
  { id: 10, name: 'Moyareklama.ru' },
  { id: 11, name: 'Domclick.ru' },
];

// Типы авторов объявлений
const PERSON_TYPES = [
  { id: 1, name: 'Частное лицо' },
  { id: 2, name: 'Агентство' },
];

// Константы для пагинации
const ADS_PER_PAGE = 100; // Количество объявлений на странице

// Конфигурация параметров количества комнат для квартир
const ROOMS_PARAMS_CONFIG = {
  // Квартиры
  2: {
    1: {
      // Продам
      roomsParam: 1945,
      hasRoomsParam: true,
    },
    2: {
      // Сдам
      roomsParam: 2019,
      hasRoomsParam: true,
    },
    3: {
      // Куплю
      roomsParam: 2085,
      hasRoomsParam: true,
    },
    4: {
      // Сниму
      roomsParam: 2101,
      hasRoomsParam: true,
    },
  },
  // Для других категорий количество комнат не применимо
  3: {
    1: { hasRoomsParam: false },
    2: { hasRoomsParam: false },
    3: { hasRoomsParam: false },
    4: { hasRoomsParam: false },
  },
  4: {
    1: { hasRoomsParam: false },
    2: { hasRoomsParam: false },
    3: { hasRoomsParam: false },
    4: { hasRoomsParam: false },
  },
  5: {
    1: { hasRoomsParam: false },
    2: { hasRoomsParam: false },
    3: { hasRoomsParam: false },
    4: { hasRoomsParam: false },
  },
  6: {
    1: { hasRoomsParam: false },
    2: { hasRoomsParam: false },
    3: { hasRoomsParam: false },
    4: { hasRoomsParam: false },
  },
  7: {
    1: { hasRoomsParam: false },
    2: { hasRoomsParam: false },
    3: { hasRoomsParam: false },
    4: { hasRoomsParam: false },
  },
  8: {
    1: { hasRoomsParam: false },
    2: { hasRoomsParam: false },
    3: { hasRoomsParam: false },
    4: { hasRoomsParam: false },
  },
};

// Конфигурация параметров площади для разных типов недвижимости и сделок
const AREA_PARAMS_CONFIG = {
  // Квартиры
  2: {
    1: {
      // Продам
      areaFromParam: 1960,
      areaToParam: 1979,
      areaLabel: 'Площадь',
      hasAreaParams: true,
    },
    2: {
      // Сдам
      areaFromParam: 2031,
      areaToParam: 2049,
      areaLabel: 'Площадь',
      hasAreaParams: true,
    },
    3: {
      // Куплю
      hasAreaParams: false,
    },
    4: {
      // Сниму
      hasAreaParams: false,
    },
  },
  // Комнаты
  3: {
    1: {
      // Продам
      areaFromParam: 2519,
      areaToParam: 2532,
      areaLabel: 'Площадь комнаты',
      hasAreaParams: true,
    },
    2: {
      // Сдам
      areaFromParam: 2577,
      areaToParam: 2590,
      areaLabel: 'Площадь комнаты',
      hasAreaParams: true,
    },
    3: {
      // Куплю
      hasAreaParams: false,
    },
    4: {
      // Сниму
      hasAreaParams: false,
    },
  },
  // Дома, дачи, коттеджи
  4: {
    1: {
      // Продам
      areaFromParam: 3047,
      areaToParam: 3075,
      areaLabel: 'Площадь дома',
      hasAreaParams: true,
    },
    2: {
      // Сдам
      areaFromParam: 3436,
      areaToParam: 3464,
      areaLabel: 'Площадь дома',
      hasAreaParams: true,
    },
    3: {
      // Куплю
      hasAreaParams: false,
    },
    4: {
      // Сниму
      hasAreaParams: false,
    },
  },
  // Земельные участки
  5: {
    1: {
      // Продам
      areaFromParam: 4197,
      areaToParam: 4229,
      areaLabel: 'Площадь участка',
      hasAreaParams: true,
    },
    2: {
      // Сдам
      areaFromParam: 4322,
      areaToParam: 4354,
      areaLabel: 'Площадь участка',
      hasAreaParams: true,
    },
    3: {
      // Куплю
      hasAreaParams: false,
    },
    4: {
      // Сниму
      hasAreaParams: false,
    },
  },
  // Гаражи и машиноместа
  6: {
    1: {
      // Продам
      areaFromParam: 12889,
      areaToParam: 12890,
      areaLabel: 'Площадь',
      hasAreaParams: true,
    },
    2: {
      // Сдам
      areaFromParam: 12891,
      areaToParam: 12892,
      areaLabel: 'Площадь',
      hasAreaParams: true,
    },
    3: {
      // Куплю
      hasAreaParams: false,
    },
    4: {
      // Сниму
      hasAreaParams: false,
    },
  },
  // Коммерческая недвижимость
  7: {
    1: {
      // Продам
      areaFromParam: 7446,
      areaToParam: 7486,
      areaLabel: 'Площадь',
      hasAreaParams: true,
    },
    2: {
      // Сдам
      areaFromParam: 7526,
      areaToParam: 7566,
      areaLabel: 'Площадь',
      hasAreaParams: true,
    },
    3: {
      // Куплю
      hasAreaParams: false,
    },
    4: {
      // Сниму
      hasAreaParams: false,
    },
  },
  // Недвижимость за рубежом - по умолчанию нет параметров
  8: {
    1: { hasAreaParams: false },
    2: { hasAreaParams: false },
    3: { hasAreaParams: false },
    4: { hasAreaParams: false },
  },
};

// Компонент для просмотра изображений
const ImageCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Преобразуем формат изображений для нового API
  const imageArray = images ? images.map((img) => img.imgurl) : [];

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % imageArray.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + imageArray.length) % imageArray.length);
  };

  if (imageArray.length === 0) return <Typography>Нет изображений</Typography>;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '400px' }}>
      <CardMedia
        component="img"
        image={imageArray[currentIndex]}
        alt={`Изображение ${currentIndex + 1}`}
        sx={{
          width: '100%',
          height: '400px',
          objectFit: 'contain',
          bgcolor: 'black',
        }}
      />

      {imageArray.length > 1 && (
        <>
          <IconButton
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.7)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
            }}>
            <NavigateBeforeIcon />
          </IconButton>

          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.7)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
            }}>
            <NavigateNextIcon />
          </IconButton>

          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
            }}>
            {currentIndex + 1} / {imageArray.length}
          </Box>
        </>
      )}
    </Box>
  );
};

function ParserPage() {
	const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [ads, setAds] = useState([]);
  const [selectedAd, setSelectedAd] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [preparedObject, setPreparedObject] = useState(null);
  const [objectFields, setObjectFields] = useState(null);
  const formRef = useRef(null);

  // Ключ для localStorage
  const FILTERS_STORAGE_KEY = 'parser_filters';

  // Значения по умолчанию для фильтров
  const defaultFilters = {
    city: 'Москва',
    categoryId: 2, // По умолчанию Квартиры
    propertyTypeId: 1, // По умолчанию Продам
    price1: '', // Цена от
    price2: '', // Цена до
    date1: '', // Дата от
    date2: '', // Дата до
    areaFrom: '', // Площадь от
    areaTo: '', // Площадь до
    roomsCount: '', // Количество комнат
    selectedSources: [], // Выбранные источники (пусто = все)
    personType: '', // Тип автора (пусто = все)
  };

  // Функция для загрузки фильтров из localStorage
  const loadFiltersFromStorage = () => {
    try {
      removeLegacyOrganizationStorage(FILTERS_STORAGE_KEY);
      const savedFilters = readOrganizationStorage(FILTERS_STORAGE_KEY);
      if (savedFilters) {
        return { ...defaultFilters, ...JSON.parse(savedFilters) };
      }
    } catch (error) {
      logger.error('Ошибка при загрузке фильтров из localStorage:', error);
    }
    return defaultFilters;
  };

  // Функция для сохранения фильтров в localStorage
  const saveFiltersToStorage = (filters) => {
    try {
      writeOrganizationStorage(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      logger.error('Ошибка при сохранении фильтров в localStorage:', error);
    }
  };

  // Инициализация состояний с загрузкой из localStorage
  const initialFilters = loadFiltersFromStorage();
  
  // Состояния для фильтров
  const [city, setCity] = useState(initialFilters.city);
  const [categoryId, setCategoryId] = useState(initialFilters.categoryId);
  const [propertyTypeId, setPropertyTypeId] = useState(initialFilters.propertyTypeId);
  const [price1, setPrice1] = useState(initialFilters.price1);
  const [price2, setPrice2] = useState(initialFilters.price2);
  const [date1, setDate1] = useState(initialFilters.date1);
  const [date2, setDate2] = useState(initialFilters.date2);
  const [areaFrom, setAreaFrom] = useState(initialFilters.areaFrom);
  const [areaTo, setAreaTo] = useState(initialFilters.areaTo);
  const [roomsCount, setRoomsCount] = useState(initialFilters.roomsCount);
  const [selectedSources, setSelectedSources] = useState(initialFilters.selectedSources);
  const [personType, setPersonType] = useState(initialFilters.personType);
  const [hasMoreAds, setHasMoreAds] = useState(false); // Есть ли еще объявления
  const [lastAdTime, setLastAdTime] = useState(null); // Время последнего объявления для пагинации

  // Получаем конфигурацию параметров площади для текущей комбинации
  const getCurrentAreaConfig = () => {
    const categoryConfig = AREA_PARAMS_CONFIG[categoryId];
    if (!categoryConfig) return null;
    return categoryConfig[propertyTypeId] || null;
  };

  // Получаем конфигурацию параметров количества комнат для текущей комбинации
  const getCurrentRoomsConfig = () => {
    const categoryConfig = ROOMS_PARAMS_CONFIG[categoryId];
    if (!categoryConfig) return null;
    return categoryConfig[propertyTypeId] || null;
  };

  // Сохраняем фильтры в localStorage при их изменении
  useEffect(() => {
    const currentFilters = {
      city,
      categoryId,
      propertyTypeId,
      price1,
      price2,
      date1,
      date2,
      areaFrom,
      areaTo,
      roomsCount,
      selectedSources,
      personType,
    };
    saveFiltersToStorage(currentFilters);
  }, [
    city,
    categoryId,
    propertyTypeId,
    price1,
    price2,
    date1,
    date2,
    areaFrom,
    areaTo,
    roomsCount,
    selectedSources,
    personType,
  ]);

  // Очищаем поля площади и комнат при изменении категории или типа сделки
  useEffect(() => {
    setAreaFrom('');
    setAreaTo('');
    setRoomsCount('');
  }, [categoryId, propertyTypeId]);

  // Сбрасываем пагинацию при изменении любых фильтров
  useEffect(() => {
    setHasMoreAds(false);
    setLastAdTime(null);
  }, [
    city,
    categoryId,
    propertyTypeId,
    selectedSources,
    personType,
    price1,
    price2,
    date1,
    date2,
    areaFrom,
    areaTo,
    roomsCount,
  ]);

  const handleParseRequest = async (isNextPage = false) => {
    try {
      setLoading(true);
      setError(null);

      // Если это первый запрос, очищаем данные
      if (!isNextPage) {
        setAds([]);
        setHasMoreAds(false);
        setLastAdTime(null);
      }

      // Формируем параметры запроса
      const params = {
        city: city,
        category_id: categoryId,
        nedvigimost_type: propertyTypeId,
        limit: ADS_PER_PAGE,
      };

      // Добавляем параметры цены, если они указаны
      if (price1 && price1.trim() !== '') {
        params.price1 = parseInt(price1);
      }
      if (price2 && price2.trim() !== '') {
        params.price2 = parseInt(price2);
      }

      // Добавляем параметры дат
      if (isNextPage && lastAdTime) {
        // Для продолжения - используем время последнего объявления как date2 (до этой даты)
        // Это должно вернуть более старые объявления
        params.date2 = lastAdTime;
        logger.log(
          'Пагинация: используем время последнего объявления как date2 (до этой даты):',
          lastAdTime,
        );
      } else {
        // Для первого запроса - используем пользовательские даты, если указаны
        if (date1 && date1.trim() !== '') {
          const formattedDate1 = new Date(date1).toISOString().slice(0, 19).replace('T', ' ');
          params.date1 = formattedDate1;
        }
        if (date2 && date2.trim() !== '') {
          const formattedDate2 = new Date(date2).toISOString().slice(0, 19).replace('T', ' ');
          params.date2 = formattedDate2;
        }

        logger.log('Первый запрос с пользовательскими датами:', {
          date1: params.date1,
          date2: params.date2,
        });
      }

      // Добавляем источники, если выбраны (если пусто - используем все)
      if (selectedSources.length > 0) {
        params.source = selectedSources.join(',');
        logger.log('Выбранные источники:', selectedSources, 'Параметр source:', params.source);
      }

      // Добавляем тип автора, если выбран (если пусто - все типы)
      if (personType && personType.trim() !== '') {
        params.person_type = parseInt(personType);
        logger.log(
          'Выбранный тип автора:',
          personType,
          'Параметр person_type:',
          params.person_type,
        );
      }

      // Добавляем параметры площади, если они доступны и указаны
      const areaConfig = getCurrentAreaConfig();
      logger.log('Конфигурация площади:', areaConfig);
      logger.log('Значения площади:', { areaFrom, areaTo });

      if (areaConfig && areaConfig.hasAreaParams) {
        if (areaFrom && areaFrom.trim() !== '') {
          const paramKey = `param[${areaConfig.areaFromParam}]`;
          params[paramKey] = parseInt(areaFrom);
          logger.log(`Добавлен параметр площади от: ${paramKey} = ${parseInt(areaFrom)}`);
        }
        if (areaTo && areaTo.trim() !== '') {
          const paramKey = `param[${areaConfig.areaToParam}]`;
          params[paramKey] = parseInt(areaTo);
          logger.log(`Добавлен параметр площади до: ${paramKey} = ${parseInt(areaTo)}`);
        }
      }

      // Добавляем параметр количества комнат, если он доступен и указан
      const roomsConfig = getCurrentRoomsConfig();
      logger.log('Конфигурация комнат:', roomsConfig);
      logger.log('Значение количества комнат:', roomsCount);

      if (roomsConfig && roomsConfig.hasRoomsParam && roomsCount && roomsCount.trim() !== '') {
        const paramKey = `param[${roomsConfig.roomsParam}]`;
        // Если выбрана "Студия", передаем строку "Студия", иначе число
        let roomsValue;
        if (roomsCount === 'studio') {
          roomsValue = 'Студия';
        } else if (roomsCount === '6') {
          roomsValue = 6; // Для "6+ комнат" передаем число 6
        } else {
          roomsValue = parseInt(roomsCount);
        }
        params[paramKey] = roomsValue;
        logger.log(`Добавлен параметр количества комнат: ${paramKey} = ${roomsValue}`);
      }

      logger.log('Параметры запроса:', { isNextPage, lastAdTime, limit: ADS_PER_PAGE });
      logger.log('Итоговые параметры запроса:', params);

      // Используем apiClient для правильной авторизации с параметрами фильтров
      const result = await apiClient.get('/api/parser/rest-app', { params });

      const data = result.data;
      setResponse(data);

      if (data.success && data.data && data.data.data) {
        const newAds = data.data.data;

        if (isNextPage) {
          // Для продолжения - добавляем к существующим объявлениям
          setAds((prevAds) => {
            const totalAfterAdd = prevAds.length + newAds.length;
            logger.log(
              `Добавляем ${newAds.length} объявлений к ${prevAds.length} существующим. Итого: ${totalAfterAdd}`,
            );
            return [...prevAds, ...newAds];
          });
        } else {
          // Для первого запроса - заменяем объявления
          setAds(newAds);
          logger.log(`Первый запрос: загружено ${newAds.length} объявлений`);
        }

        // Определяем есть ли еще объявления
        setHasMoreAds(newAds.length === ADS_PER_PAGE);

        // Сохраняем время последнего объявления для пагинации
        if (newAds.length > 0) {
          const lastAd = newAds[newAds.length - 1];
          setLastAdTime(lastAd.time);
          logger.log('Время последнего объявления:', lastAd.time);
          logger.log('Заголовок последнего объявления:', lastAd.title);
          logger.log(
            'Первые 3 объявления:',
            newAds.slice(0, 3).map((ad) => ({ title: ad.title, time: ad.time })),
          );
          logger.log(
            'Последние 3 объявления:',
            newAds.slice(-3).map((ad) => ({ title: ad.title, time: ad.time })),
          );
        }

        logger.log('Количество новых объявлений:', newAds.length);
        logger.log('Есть еще объявления:', newAds.length === ADS_PER_PAGE);
      }

      logger.log('Ответ от парсера получен:', data);
    } catch (err) {
      setError(err.message);
      logger.error('Ошибка при запросе к парсеру:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (ad) => {
    setSelectedAd(ad);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleAddToBase = () => {
    logger.log('🚩🚩🚩 Добавление в базу:', selectedAd);

    const preparedObject = prepareParseObject(selectedAd);
    logger.log('Prepared object:', preparedObject);

    const fields = objectStore.jsonfiles.find(
      (file) => file.type.fileName === preparedObject.details.jsonFileName,
    );

    // Сохраняем объект и поля в состоянии
    setPreparedObject(preparedObject);
    setObjectFields(fields);

    // Открываем модальное окно для редактирования полей
    setEditModalOpen(true);
  };

  // Получаем параметр по имени
  const getParamValue = (params, name) => {
    if (!params) return 'Не указано';

    // В новом API параметры могут быть в разных форматах
    // Проверяем оба варианта
    if (params[name]) {
      return params[name];
    }

    return 'Не указано';
  };

  // Форматируем адрес
  const formatAddress = (ad) => {
    const parts = [];
    if (ad.region) parts.push(ad.region);
    if (ad.city1) parts.push(ad.city1);
    if (ad.address) parts.push(ad.address);

    return parts.join(', ');
  };

  // Получаем название категории по ID
  const getCategoryName = (id) => {
    const category = CATEGORIES.find((cat) => cat.id === id);
    return category ? category.name : 'Не указано';
  };

  // Получаем название типа сделки по ID
  const getPropertyTypeName = (id) => {
    const type = PROPERTY_TYPES.find((t) => t.id === parseInt(id));
    return type ? type.name : 'Не указано';
  };

  // Обработчик выбора источников
  const handleSourcesChange = (event) => {
    const value = event.target.value;

    // Если выбрано "Все" - очищаем массив (будут использоваться все источники)
    if (value.includes('all')) {
      setSelectedSources([]);
      return;
    }

    setSelectedSources(typeof value === 'string' ? value.split(',') : value);
  };

  // Получаем названия выбранных источников для отображения
  const getSelectedSourcesNames = () => {
    if (selectedSources.length === 0) return 'Все источники';
    return selectedSources
      .map((id) => {
        const source = SOURCES.find((s) => s.id === parseInt(id));
        return source ? source.name : id;
      })
      .join(', ');
  };

  // Получаем название типа автора для отображения
  const getPersonTypeName = () => {
    if (!personType || personType.trim() === '') return 'Все типы';
    const type = PERSON_TYPES.find((t) => t.id === parseInt(personType));
    return type ? type.name : 'Все типы';
  };

  // Обработчик для кнопки "Далее"
  const handleNextPage = () => {
    handleParseRequest(true);
  };

  // Функция сброса фильтров к значениям по умолчанию
  const handleResetFilters = () => {
    setCity(defaultFilters.city);
    setCategoryId(defaultFilters.categoryId);
    setPropertyTypeId(defaultFilters.propertyTypeId);
    setPrice1(defaultFilters.price1);
    setPrice2(defaultFilters.price2);
    setDate1(defaultFilters.date1);
    setDate2(defaultFilters.date2);
    setAreaFrom(defaultFilters.areaFrom);
    setAreaTo(defaultFilters.areaTo);
    setRoomsCount(defaultFilters.roomsCount);
    setSelectedSources(defaultFilters.selectedSources);
    setPersonType(defaultFilters.personType);
    
    // Очищаем результаты поиска
    setAds([]);
    setHasMoreAds(false);
    setLastAdTime(null);
    setResponse(null);
    setError(null);
    
    logger.log('Фильтры сброшены к значениям по умолчанию');
  };

  const handleSaveObject = async () => {
    if (formRef.current) {
      const result = await formRef.current.postNewParsedObject();
      if (result?.success) {
        setEditModalOpen(false);
        setError(null);
		setResponse(result.alreadyImported
			? { success: true, alreadyImported: true, objectId: result.data.object.id }
			: { success: true, message: 'Объект успешно добавлен в базу!' });
		return;
      }
		setError(result?.error || 'Не удалось сохранить объект.');
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      {import.meta.env.VITE_SHOW_COMPONENT_NAME === 'true' && (
        <div onClick={() => logger.log(ads)}>% ParserPage %</div>
      )}
      <Typography variant="h4" gutterBottom>
        Парсер объявлений
      </Typography>

      {/* Фильтры для поиска */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Фильтры поиска
        </Typography>

        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Город или регион"
              variant="outlined"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Например: Москва, Санкт-Петербург"
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Категория недвижимости</InputLabel>
              <Select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                input={<OutlinedInput label="Категория недвижимости" />}>
                {CATEGORIES.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Тип сделки</InputLabel>
              <Select
                value={propertyTypeId}
                onChange={(e) => setPropertyTypeId(e.target.value)}
                input={<OutlinedInput label="Тип сделки" />}>
                {PROPERTY_TYPES.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>


          
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Источники объявлений</InputLabel>
                <Select
                  multiple
                  size="small"
                  value={selectedSources}
                  onChange={handleSourcesChange}
                  input={<OutlinedInput label="Источники объявлений" />}
                  renderValue={() => getSelectedSourcesNames()}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}>
                  <MenuItem key="all" value="all">
                    <Checkbox checked={selectedSources.length === 0} />
                    <ListItemText primary="Все источники" />
                  </MenuItem>
                  <Divider />
                  {SOURCES.map((source) => (
                    <MenuItem key={source.id} value={source.id.toString()}>
                      <Checkbox checked={selectedSources.includes(source.id.toString())} />
                      <ListItemText primary={source.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Тип автора объявления</InputLabel>
                <Select
                  size="small"
                  value={personType}
                  onChange={(e) => setPersonType(e.target.value)}
                  input={<OutlinedInput label="Тип автора объявления" />}>
                  <MenuItem value="">Все типы</MenuItem>
                  {PERSON_TYPES.map((type) => (
                    <MenuItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
      


          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Цена от (₽)"
              variant="outlined"
              value={price1}
              onChange={(e) => setPrice1(e.target.value)}
              placeholder="Например: 1000000"
              type="number"
              size="small"
              InputProps={{
                inputProps: { min: 0 },
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Цена до (₽)"
              variant="outlined"
              value={price2}
              onChange={(e) => setPrice2(e.target.value)}
              placeholder="Например: 5000000"
              type="number"
              InputProps={{
                inputProps: { min: 0 },
              }}
            />
          </Grid>
          {getCurrentAreaConfig()?.hasAreaParams && (
            <>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={`${getCurrentAreaConfig().areaLabel} от (м²)`}
                  variant="outlined"
                  value={areaFrom}
                  onChange={(e) => setAreaFrom(e.target.value)}
                  placeholder="Например: 30"
                  type="number"
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={`${getCurrentAreaConfig().areaLabel} до (м²)`}
                  variant="outlined"
                  value={areaTo}
                  onChange={(e) => setAreaTo(e.target.value)}
                  placeholder="Например: 100"
                  type="number"
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>
            </>
          )}

         

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Дата от"
              variant="outlined"
              value={date1}
              onChange={(e) => setDate1(e.target.value)}
              type="datetime-local"
              InputLabelProps={{
                shrink: true,
              }}
              helperText="Время публикации объявлений от указанной даты"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Дата до"
              variant="outlined"
              value={date2}
              onChange={(e) => setDate2(e.target.value)}
              type="datetime-local"
              InputLabelProps={{
                shrink: true,
              }}
              helperText="Время публикации объявлений до указанной даты"
            />
          </Grid>
          {getCurrentRoomsConfig()?.hasRoomsParam && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Количество комнат</InputLabel>
                <Select
                  value={roomsCount}
                  onChange={(e) => setRoomsCount(e.target.value)}
                  label="Количество комнат"
                >
                  <MenuItem value="">Любое количество</MenuItem>
                  <MenuItem value="studio">Студия</MenuItem>
                  <MenuItem value="1">1 комната</MenuItem>
                  <MenuItem value="2">2 комнаты</MenuItem>
                  <MenuItem value="3">3 комнаты</MenuItem>
                  <MenuItem value="4">4 комнаты</MenuItem>
                  <MenuItem value="5">5 комнат</MenuItem>
                  <MenuItem value="6">6+ комнат</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleParseRequest(false)}
            disabled={loading}
            sx={{ flex: 1 }}>
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Найти объявления'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleResetFilters}
            disabled={loading}
            sx={{ minWidth: 'auto', px: 3 }}>
            Сбросить фильтры
          </Button>
        </Box>
      </Paper>

      {/* Панель информации */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Текущие параметры поиска:
        </Typography>
        <Typography variant="body2">• Город: {city}</Typography>
        <Typography variant="body2">• Категория: {getCategoryName(categoryId)}</Typography>
        <Typography variant="body2">• Тип сделки: {getPropertyTypeName(propertyTypeId)}</Typography>
        <Typography variant="body2">• Источники: {getSelectedSourcesNames()}</Typography>
        <Typography variant="body2">• Тип автора: {getPersonTypeName()}</Typography>

        {price1 && (
          <Typography variant="body2">
            • Цена от: {new Intl.NumberFormat('ru-RU').format(price1)} ₽
          </Typography>
        )}
        {price2 && (
          <Typography variant="body2">
            • Цена до: {new Intl.NumberFormat('ru-RU').format(price2)} ₽
          </Typography>
        )}
        {date1 && (
          <Typography variant="body2">
            • Дата от: {new Date(date1).toLocaleString('ru-RU')}
          </Typography>
        )}
        {date2 && (
          <Typography variant="body2">
            • Дата до: {new Date(date2).toLocaleString('ru-RU')}
          </Typography>
        )}
        {getCurrentAreaConfig()?.hasAreaParams && areaFrom && (
          <Typography variant="body2">
            • {getCurrentAreaConfig().areaLabel} от:{' '}
            {new Intl.NumberFormat('ru-RU').format(areaFrom)} м²
          </Typography>
        )}
        {getCurrentAreaConfig()?.hasAreaParams && areaTo && (
          <Typography variant="body2">
            • {getCurrentAreaConfig().areaLabel} до: {new Intl.NumberFormat('ru-RU').format(areaTo)}{' '}
            м²
          </Typography>
        )}
        {getCurrentRoomsConfig()?.hasRoomsParam && roomsCount && (
          <Typography variant="body2">
            • Количество комнат: {roomsCount === 'studio' ? 'Студия' : roomsCount === '6' ? '6+ комнат' : `${roomsCount} ${roomsCount === '1' ? 'комната' : roomsCount <= '4' ? 'комнаты' : 'комнат'}`}
          </Typography>
        )}
        <Typography variant="body2">• Лимит: {ADS_PER_PAGE} объявлений на страницу</Typography>
        {ads.length > 0 && (
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            • Показано объявлений: {ads.length.toLocaleString('ru-RU')}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          Ошибка: {error}
        </Alert>
      )}

      {response && response.success && response.alreadyImported && (
        <Alert
          severity="info"
          style={{ marginBottom: '20px' }}
          action={<Button color="inherit" size="small" onClick={() => navigate(`/objects?editObjectId=${encodeURIComponent(response.objectId)}`)}>Открыть объект</Button>}>
          Это объявление уже было импортировано. Новый объект не создан.
        </Alert>
      )}

      {response && response.success && !response.alreadyImported && (
        <Alert severity="success" style={{ marginBottom: '20px' }}>
          {response.message || 'Статус: успешно получены данные'}
          {ads.length > 0 ? `, показано объявлений: ${ads.length}` : ''}
          {hasMoreAds ? ' (доступны дополнительные объявления)' : ''}
        </Alert>
      )}

      {ads.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Результаты поиска:
          </Typography>

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="результаты парсинга">
              <TableHead>
                <TableRow>
                  <TableCell>Заголовок</TableCell>
                  <TableCell>Цена</TableCell>
                  <TableCell>Город</TableCell>
                  <TableCell>Адрес</TableCell>
                  <TableCell>Детали</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ads.map((ad, index) => (
                  <TableRow
                    key={`${ad.id}-${index}`}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {ad.source === 'cian.ru' && (
                          <Avatar
                            src="/logo_cian.png"
                            alt="Циан"
                            sx={{ width: 16, height: 16, mr: 1 }}
                          />
                        )}
                        {ad.source === 'avito.ru' && (
                          <Avatar
                            src="/logo_avito.png"
                            alt="Авито"
                            sx={{ width: 16, height: 16, mr: 1 }}
                          />
                        )}
                        {ad.source === 'realty.yandex.ru' && (
                          <Avatar
                            src="/logo_yandex.png"
                            alt="Яндекс недвижимость"
                            sx={{ width: 16, height: 16, mr: 1 }}
                          />
                        )}
                        {ad.source === 'domclick.ru' && (
                          <Avatar
                            src="/logo_domclick.png"
                            alt="Домклик"
                            sx={{ width: 16, height: 16, mr: 1 }}
                          />
                        )}
                        {ad.source === 'youla.io' && (
                          <Avatar
                            src="/logo_youla.png"
                            alt="Юла"
                            sx={{ width: 16, height: 16, mr: 1 }}
                          />
                        )}
                        {ad.source === 'sob.ru' && (
                          <Avatar
                            src="/logo_sob.png"
                            alt="Sob.ru"
                            sx={{ width: 16, height: 16, mr: 1 }}
                          />
                        )}
                        {ad.title}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Intl.NumberFormat('ru-RU').format(ad.price)} {ad.price_metric || '₽'}
                    </TableCell>
                    <TableCell>{ad.city1}</TableCell>
                    <TableCell>{ad.address || 'Не указан'}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleOpenModal(ad)}>
                        Посмотреть
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Кнопка "Далее" для продолжения загрузки */}
          {hasMoreAds && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleNextPage}
                disabled={loading}
                size="small"
                sx={{ px: 4, py: 1.5 }}>
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Загрузка...
                  </>
                ) : (
                  'Загрузить еще >>'
                )}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Модальное окно с детальной информацией об объявлении */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="ad-details-modal"
        aria-describedby="detailed-information-about-ad">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 1200,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 0,
            borderRadius: 1,
            overflow: 'auto',
          }}>
          {selectedAd && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  p: 2,
                  borderBottom: '1px solid #eee',
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 1,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {selectedAd.source === 'cian.ru' && (
                    <Avatar src="/logo_cian.png" alt="Циан" sx={{ width: 32, height: 32, mr: 2 }} />
                  )}
                  {selectedAd.source === 'domclick.ru' && (
                    <Avatar
                      src="/logo_domclick.png"
                      alt="Домклик"
                      sx={{ width: 32, height: 32, mr: 2 }}
                    />
                  )}
                  {selectedAd.source === 'realty.yandex.ru' && (
                    <Avatar
                      src="/logo_yandex.png"
                      alt="Яндекс недвижимость"
                      sx={{ width: 32, height: 32, mr: 2 }}
                    />
                  )}
                  {selectedAd.source === 'youla.io' && (
                    <Avatar src="/logo_youla.png" alt="Юла" sx={{ width: 32, height: 32, mr: 2 }} />
                  )}
                  {selectedAd.source === 'sob.ru' && (
                    <Avatar src="/logo_sob.png" alt="Юла" sx={{ width: 32, height: 32, mr: 2 }} />
                  )}
                  {selectedAd.source === 'avito.ru' && (
                    <Avatar
                      src="/logo_avito.png"
                      alt="Авито"
                      sx={{ width: 32, height: 32, mr: 2 }}
                    />
                  )}
                  <Typography id="ad-details-modal" variant="h6" component="h2">
                    {selectedAd.title}
                  </Typography>
                </Box>
				<IconButton aria-label="Закрыть" onClick={handleCloseModal} sx={{ ml: 2 }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Grid container>
                {/* Галерея изображений */}
                <Grid item xs={12} md={6}>
                  <ImageCarousel images={selectedAd.images} />
                </Grid>

                {/* Информация о объявлении */}
                <Grid item xs={12} md={6} sx={{ p: 2 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {new Intl.NumberFormat('ru-RU').format(selectedAd.price)}{' '}
                    {selectedAd.price_metric || '₽'}
                  </Typography>

                  <Typography variant="body1" gutterBottom>
                    {formatAddress(selectedAd)}
                  </Typography>

                  <Box sx={{ my: 2 }}>
                    <Grid container spacing={1}>
                      {selectedAd.params && (
                        <>
                          <Grid item>
                            <Chip
                              label={`${getParamValue(
                                selectedAd.params,
                                'Количество комнат',
                              )} комн.`}
                            />
                          </Grid>
                          <Grid item>
                            <Chip label={getParamValue(selectedAd.params, 'Площадь')} />
                          </Grid>
                          <Grid item>
                            <Chip label={`${getParamValue(selectedAd.params, 'Этаж')}`} />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" gutterBottom>
                    Контактная информация:
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Имя: {selectedAd.person || selectedAd.contactname || 'Не указано'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Телефон: {selectedAd.phone || 'Не указано'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Тип: {selectedAd.person_type || 'Не указано'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Размещено: {new Date(selectedAd.time).toLocaleString('ru-RU')}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" gutterBottom>
                    Описание:
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedAd.description || 'Описание отсутствует'}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {selectedAd.params && Object.keys(selectedAd.params).length > 0 && (
                    <>
                      <Typography variant="subtitle1" gutterBottom>
                        Характеристики:
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        {Object.entries(selectedAd.params).map(([key, value], idx) => (
                          <Grid item xs={6} key={idx}>
                            <Typography variant="body2" color="text.secondary">
                              {key}:
                            </Typography>
                            <Typography variant="body2">{value}</Typography>
                          </Grid>
                        ))}
                      </Grid>
                      <Divider sx={{ my: 2 }} />
                    </>
                  )}

                  {selectedAd.params2 && Object.keys(selectedAd.params2).length > 0 && (
                    <>
                      <Typography variant="subtitle1" gutterBottom>
                        Дополнительная информация:
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        {Object.entries(selectedAd.params2).map(([category, paramGroup], idx) => (
                          <Grid item xs={12} key={idx}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontWeight: 'bold' }}>
                              {category}:
                            </Typography>
                            {Object.entries(paramGroup).map(([paramName, paramValue], subIdx) => (
                              <Box key={subIdx} sx={{ ml: 2, mb: 1 }}>
                                <Typography variant="body2" color="text.secondary" component="span">
                                  {paramName}:
                                </Typography>
                                <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                                  {paramValue}
                                </Typography>
                              </Box>
                            ))}
                          </Grid>
                        ))}
                      </Grid>
                      <Divider sx={{ my: 2 }} />
                    </>
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleAddToBase}
                    target="_blank"
                    sx={{ mt: 2 }}>
                    Добавить в базу
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    href={selectedAd.url}
                    target="_blank"
                    sx={{ mt: 2 }}>
                    Открыть оригинал
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Modal>

      {/* Модальное окно с DynamicFieldsEditParsed */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        aria-labelledby="dynamic-fields-edit-modal"
        aria-describedby="modal-for-editing-object-fields">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 1200,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
          }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: '1px solid #eee',
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
              zIndex: 1,
            }}>
            <Typography id="dynamic-fields-edit-modal" variant="h6" component="h2">
              Редактирование объекта перед добавлением
            </Typography>
			<IconButton aria-label="Закрыть" onClick={() => setEditModalOpen(false)} sx={{ ml: 2 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {preparedObject && objectFields && (
            <>
              <DynamicFieldsEditParsed
                object={preparedObject}
                fields={objectFields}
                ref={formRef}
                objectRaw={selectedAd}

              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 3,
                  p: 2,
                  borderTop: '1px solid #eee',
                  position: 'sticky',
                  bottom: 0,
                  bgcolor: 'background.paper',
                  zIndex: 1,
                }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setEditModalOpen(false)}
                  sx={{ mr: 2 }}>
                  Отмена
                </Button>
                <Button variant="contained" color="primary" onClick={handleSaveObject}>
                  Сохранить в мою базу
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
}

export default observer(ParserPage);
